import BaseAPI from './BaseAPI';

export const AGENT_NAMES = {
  CLINICAL_WORKFLOW_CHAT: 'clinical_workflow_chat',
} as const;

export type AgentName = (typeof AGENT_NAMES)[keyof typeof AGENT_NAMES] | string;

/**
 * Derive the canonical env key from the base_url returned by v4/login_check.
 * Falls back to apiEnv if baseUrl is not set.
 *
 * Examples:
 *   https://uat-temp-api.tigertext.me   → 'uat'
 *   https://temp-api.tigertext.me       → 'production'
 *   https://env7-temp-api.tigertext.xyz → 'env7'
 */
function resolveEnv(baseUrl: unknown, apiEnv: string): string {
  if (typeof baseUrl === 'string') {
    const prefix = new URL(baseUrl).hostname.split('.')[0].split('-')[0];
    if (prefix === 'api' || prefix === 'temp') return 'production';
    return prefix; // 'uat', 'env7', etc.
  }
  return apiEnv || 'production';
}

const AGENT_BASE_URLS: Record<string, string> = {
  production: 'https://agent.tigerconnect.com',
  uat: 'https://agent.uat.tigerconnect.com',
  env7: 'https://agent.env7.tigerconnect.com',
};

const IDP_TOKEN_ENDPOINTS: Record<string, string> = {
  production: 'https://idp.tigerconnect.com/connect/token',
  uat: 'https://idp.uat.tigerconnect.com/connect/token',
  env7: 'https://idp.env7.tigerconnect.com/connect/token',
};

const IDP_CLIENT_IDS: Record<string, string> = {
  production: 'web-exchange-prod',
  uat: 'web-exchange-uat',
  env7: 'web-exchange-dev',
};

export type SSEEvent =
  | { event: 'message_start'; data: { id: string; type: string } }
  | { event: 'token'; data: { text: string } }
  | { event: 'message'; data: { text: string } }
  | { event: 'tool_call'; data: { id: string; name: string; args: Record<string, unknown> } }
  | { event: 'tool_result'; data: { tool_call_id: string; name: string; content: string } }
  | { event: 'message_end'; data: { id: string; type: string } }
  | { event: 'done'; data: { messages: unknown[]; session_id: string } }
  | { event: 'error'; data: { error: string; error_type: string } };

export type InvokeAgentOptions = {
  agentName?: AgentName;
  messages: Array<{ role: string; content: string }>;
  stream?: boolean;
  sessionId?: string;
  organizationId?: string;
  signal?: AbortSignal;
};

export default class AgentCoreAPI extends BaseAPI {
  /**
   * Get the agent invocation URL for the current environment.
   * Format: https://agent.{env}.tigerconnect.com/agents/{agent_name}/invocations
   */
  getInvocationUrl(agentName: AgentName = AGENT_NAMES.CLINICAL_WORKFLOW_CHAT): string {
    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    const baseUrl = AGENT_BASE_URLS[env] || `https://agent.${env}.tigerconnect.com`;
    return `${baseUrl}/agents/${agentName}/invocations`;
  }

  /**
   * Get the IDP token endpoint for the current environment.
   */
  getIdpTokenEndpoint(): string {
    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    return IDP_TOKEN_ENDPOINTS[env] || `https://idp.${env}.tigerconnect.com/connect/token`;
  }

  /**
   * Get the IDP client ID for the current environment.
   */
  getIdpClientId(): string {
    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    return IDP_CLIENT_IDS[env] || 'web-exchange-dev';
  }

  /**
   * Exchange API key/secret for an OAuth JWT via the IDP.
   */
  async getOAuthToken(organizationId: string): Promise<string> {
    const auth = this.httpClient.getAuth();
    if (!auth) {
      throw new Error('Not authenticated — cannot exchange for OAuth token');
    }

    const body = new URLSearchParams({
      grant_type: 'api_key',
      api_key: auth.key,
      api_secret: auth.secret,
      client_id: this.getIdpClientId(),
      org_id: organizationId,
      scope: 'agent:invoke openid profile email api.internal.basic',
    });

    const response = await fetch(this.getIdpTokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      throw new Error(`OAuth token exchange failed: ${response.status}`);
    }

    const data = await response.json();
    return data.access_token;
  }

  /**
   * Invoke an AgentCore agent and stream SSE events.
   *
   * @param options - Invocation options
   * @param onEvent - Callback for each SSE event
   */
  async invoke(options: InvokeAgentOptions, onEvent: (event: SSEEvent) => void): Promise<void> {
    const { agentName, messages, stream = true, sessionId, organizationId, signal } = options;

    if (!organizationId) {
      throw new Error('organizationId is required for agent invocation');
    }

    const token = await this.getOAuthToken(organizationId);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    if (sessionId) {
      headers['X-Amzn-Bedrock-AgentCore-Runtime-Session-Id'] = sessionId;
    }

    const response = await fetch(this.getInvocationUrl(agentName), {
      method: 'POST',
      headers,
      body: JSON.stringify({ messages, stream, session_id: sessionId }),
      signal,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Agent invocation failed (${response.status}): ${errorBody}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          let eventData: SSEEvent;
          try {
            eventData = JSON.parse(trimmed.slice(6)) as SSEEvent;
          } catch {
            // skip malformed SSE lines
            continue;
          }
          onEvent(eventData);
        }
      }

      // Flush any remaining data in the buffer if the stream ended without a trailing newline
      const remaining = buffer.trim();
      if (remaining && remaining.startsWith('data: ')) {
        let eventData: SSEEvent;
        try {
          eventData = JSON.parse(remaining.slice(6)) as SSEEvent;
        } catch {
          // skip malformed SSE lines
          return;
        }
        onEvent(eventData);
      }
    } finally {
      reader.releaseLock();
    }
  }
}
